import React from "react";
import ClockLoader from "react-spinners/ClockLoader";
import { Box, Card, CardContent, Typography, Grid, Divider, Paper } from "@mui/material";

export default function Review({ formData, loading }) {
  function DateFormat(dateString) {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const month = new Intl.DateTimeFormat("en-US", { month: "short" }).format(date);
    const day = new Intl.DateTimeFormat("en-US", { day: "numeric" }).format(date);
    const year = new Intl.DateTimeFormat("en-US", { year: "numeric" }).format(date);
    return `${day}-${month}-${year}`;
  }

  function AmountFormat(amount) {
    if (amount === null || amount === undefined || amount === "") return "-";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  }

  const DetailItem = ({ label, value, md = 3, sm = 6, xs = 12 }) => (
    <Grid item xs={xs} sm={sm} md={md}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
        {value || "-"}
      </Typography>
    </Grid>
  );

  const SectionTitle = ({ title }) => (
    <Typography variant="h6" sx={{ color: "#ce690a", fontWeight: "bold", mb: 3, mt: 1, pb: 1, borderBottom: '2px solid #f0f0f0' }}>
      {title}
    </Typography>
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <ClockLoader color="#1976d2" loading={loading} size={150} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, md: 5 } }}>
          
          <SectionTitle title="Applicant Details" />
          <Grid container spacing={3}>
            <DetailItem label="First Name" value={formData.firstName} md={2} />
            <DetailItem label="Last Name" value={formData.lastName} md={2} />
            <DetailItem label="Aadhar Number" value={formData.aadharNumber} md={3} />
            <DetailItem label="Date of Birth" value={DateFormat(formData.dob)} md={2} />
            <DetailItem label="Email" value={formData.email} md={3} />
            <DetailItem label="Phone Number" value={formData.phonenumber} md={3} />
            <DetailItem label="Address" value={formData.address} md={9} />
            <DetailItem label="City" value={formData.city} md={3} />
            <DetailItem label="State" value={formData.state} md={3} />
            <DetailItem label="District" value={formData.district} md={3} />
            <DetailItem label="Pincode" value={formData.zip} md={3} />
          </Grid>

          <Box sx={{ my: 4 }} />
          
          <SectionTitle title="Parent/Guardian Details" />
          <Grid container spacing={3}>
            <DetailItem label="Father Name" value={formData.fatherName} />
            <DetailItem label="Father Occupation" value={formData.fatherOccupation} />
            <DetailItem label="Mother Name" value={formData.motherName} />
            <DetailItem label="Mother Occupation" value={formData.motherOccupation} />
            <DetailItem label="Family Income" value={AmountFormat(formData.familyIncome)} />
            <DetailItem label="Family Members" value={formData.familyCount} />
          </Grid>

          <Box sx={{ my: 4 }} />
          
          <SectionTitle title="Indian Reference Details" />
          <Grid container spacing={3}>
            <DetailItem label="Referral Name" value={formData.referralName} md={3} />
            <DetailItem label="Referral Email" value={formData.referralEmail} md={3} />
            <DetailItem label="Referral Phone" value={formData.referralPhone} md={3} />
            <DetailItem label="Relationship with Referral" value={formData.referralRelationship} md={3} />
          </Grid>

          <Box sx={{ my: 4 }} />
          
          <SectionTitle title="USA Volunteer Details" />
          <Grid container spacing={3}>
            <DetailItem label="Volunteer Name" value={formData.volunteerName} md={4} />
            <DetailItem label="Volunteer Email" value={formData.volunteerEmail} md={4} />
            <DetailItem label="Volunteer Phone" value={formData.volunteerPhone} md={4} />
          </Grid>

          <Box sx={{ my: 4 }} />
          
          <SectionTitle title="Aid Request Details" />
          <Grid container spacing={3}>
            <DetailItem label="Aid Request Amount" value={AmountFormat(formData.amountRequested)} md={3} />
            <DetailItem label="Aid Type" value={formData.aidtype} md={5} />
            {(formData.courseType || formData.finalYearOfPassing) && (
              <>
                <DetailItem label="Course Type" value={formData.courseType} md={2} />
                <DetailItem label="Final Year of Passing" value={formData.finalYearOfPassing} md={2} />
              </>
            )}
            <DetailItem label="Aid Request Reason" value={formData.reasonForRequesting} md={12} />
            <DetailItem label="Request for multiple years" value={formData.multipleyears} md={4} />
            {formData.multipleyears === "yes" && (
              <DetailItem label="How many years of support is needed" value={formData.yearsofsupportisneeded} md={4} />
            )}
          </Grid>

          <Box sx={{ my: 4 }} />

          <SectionTitle title="Institution Details" />
          <Grid container spacing={3}>
            <DetailItem label="Institution Name" value={formData.institutionName} md={4} />
            <DetailItem label="Email" value={formData.institutionEmail} md={4} />
            <DetailItem label="Mobile Number" value={formData.institutionMobileNumber} md={4} />
            <DetailItem label="Address" value={formData.institutionAddress} md={8} />
            <DetailItem label="City" value={formData.institutionCity} md={2} />
            <DetailItem label="State" value={formData.institutionState} md={2} />
            <DetailItem label="Pincode" value={formData.institutionZip} md={2} />
            <DetailItem label="Beneficiary Name" value={formData.beneficiaryName} md={5} />
            <DetailItem label="Beneficiary Phone" value={formData.beneficiaryPhone} md={5} />
          </Grid>

          <Box sx={{ my: 4 }} />

          <SectionTitle title="School / College Bank Details" />
          <Grid container spacing={3}>
            <DetailItem label="Account Number" value={formData.accountNumber} md={3} />
            <DetailItem label="IFSC Code" value={formData.ifscCode} md={2} />
            <DetailItem label="Bank Name" value={formData.bankName} md={3} />
            <DetailItem label="Branch Name" value={formData.branchName} md={4} />
            <DetailItem label="Branch Address" value={formData.paymentAddress} md={12} />
          </Grid>
          
        </CardContent>
      </Paper>
    </Box>
  );
}
